import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import { Bar, Line } from "react-chartjs-2";
import { Chart as ChartJS, Colors } from "chart.js";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import ThreatWordCloud from "../../components/ThreatWordCloud.jsx";
import TickerWidget from "../../components/TickerWidget.jsx";
import RiskWidget from "../../components/RiskWidget.jsx";
import DisplayCommentsChart from "../../components/CommentsChart.jsx";
import TopMetrics from "../../components/TopMetrics.jsx";
import { PopOutChartModal } from "../../modals/chartPopOutModal.jsx";

import "./dashboard.css";

ChartJS.register(Colors);

class WordCloudErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error("ThreatWordCloud render error:", error);
  }

  componentDidUpdate(previousProps) {
    if (previousProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

const DashboardLoadingState = ({ fullPage = false }) => (
  <div
    className={`analytics-loading ${fullPage ? "analytics-loading--page" : ""}`}
    role="status"
    aria-live="polite"
  >
    <span className="analytics-loading-label">Updating analytical view</span>
    <span className="analytics-loading-dots" aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  </div>
);

const EmptyState = ({ onReviewAssets, message }) => (
  <div className="analytics-empty-state">
    <strong>No activity is available for this view.</strong>
    <p>
      {message ||
        "Adjust the current filters or confirm that monitored assets are configured for the selected group."}
    </p>
    <button type="button" onClick={onReviewAssets}>
      Review monitored assets
    </button>
  </div>
);

const StableWordCloud = ({ data, loading, onReviewAssets }) => {
  const containerRef = useRef(null);
  const [isSized, setIsSized] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const measure = () => {
      const bounds = element.getBoundingClientRect();
      setIsSized(bounds.width >= 100 && bounds.height >= 100);
    };

    measure();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const hasUsableData =
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    Object.keys(data).length > 0;

  const resetKey = useMemo(() => {
    if (!hasUsableData) return "empty";
    return Object.keys(data).sort().join("|");
  }, [data, hasUsableData]);

  return (
    <div className="analytics-card-body analytics-word-cloud-body">
      <div ref={containerRef} className="analytics-word-cloud-canvas">
        {loading || !isSized ? (
          <DashboardLoadingState />
        ) : !hasUsableData ? (
          <EmptyState onReviewAssets={onReviewAssets} />
        ) : (
          <WordCloudErrorBoundary
            resetKey={resetKey}
            fallback={
              <EmptyState
                onReviewAssets={onReviewAssets}
                message="The language visualization could not be displayed. The underlying collected data remains available in the Threat Feed."
              />
            }
          >
            <ThreatWordCloud data={data} />
          </WordCloudErrorBoundary>
        )}
      </div>
    </div>
  );
};

const AnalyticsCard = ({
  kicker,
  title,
  onExpand,
  expandLabel,
  infoText,
  bodyClassName = "",
  children,
}) => (
  <section className="analytics-card">
    <header className="analytics-card-header">
      <div className="analytics-card-action">
        {onExpand ? (
          <button
            type="button"
            className="analytics-expand-button"
            aria-label={expandLabel || `Open ${title} in expanded view`}
            title="Open expanded view"
            onClick={onExpand}
          >
            <OpenInNewIcon aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <div className="analytics-card-title">
        <span>{kicker}</span>
        <h2>{title}</h2>
      </div>

      <div className="analytics-card-meta">
        {infoText ? (
          <span
            className="analytics-info-badge"
            aria-label="More information"
            title={infoText}
          >
            i
          </span>
        ) : null}
      </div>
    </header>

    <div className={`analytics-card-body ${bodyClassName}`.trim()}>{children}</div>
  </section>
);

export const Dashboard = () => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const [userToken, setUserToken] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [wordCloudLoading, setWordCloudLoading] = useState(false);

  const [selectDate, setSelectDate] = useState("");
  const [debouncedDate, setDebouncedDate] = useState("");
  const [group, setGroup] = useState("none");
  const [asset, setAsset] = useState("none");
  const [groupData, setGroupData] = useState([]);
  const [groupAssets, setGroupAssets] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    allStats: {},
    individualStats: [],
    comsChart: null,
    timeLineData: null,
    tickerData: [],
    newsData: [],
    barData: [],
    comStatistics: null,
  });

  const [filteredIndividualStats, setFilteredIndividualStats] = useState([]);
  const [threatData, setThreatData] = useState(null);

const [popOutChartType, setPopOutChartType] = useState("BAR");
const [chartPopOutOpen, setChartPopOutOpen] = useState(false);
const [chartPopOutTitle, setChartPopOutTitle] = useState("");
const [chartPopOutData, setChartPopOutData] = useState({
  labels: [],
  datasets: [],
});

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      animation: {
        duration: 300,
      },
      elements: {
        line: {
          tension: 0.34,
          borderWidth: 2,
        },
        point: {
          radius: 2,
          hoverRadius: 5,
        },
      },
      plugins: {
        legend: {
          display: true,
          position: "top",
          align: "start",
          labels: {
            color: "rgba(244, 247, 251, 0.70)",
            boxWidth: 12,
            boxHeight: 3,
            padding: 16,
            usePointStyle: true,
            pointStyle: "line",
            font: {
              size: 11,
            },
          },
        },
        tooltip: {
          backgroundColor: "rgba(7, 14, 24, 0.96)",
          borderColor: "rgba(255, 255, 255, 0.14)",
          borderWidth: 1,
          titleColor: "#f4f7fb",
          bodyColor: "rgba(244, 247, 251, 0.78)",
          padding: 12,
          displayColors: true,
        },
        colors: {
          enabled: true,
          forceOverride: true,
        },
      },
      scales: {
        x: {
          grid: {
            color: "rgba(255, 255, 255, 0.035)",
          },
          border: {
            color: "rgba(255, 255, 255, 0.08)",
          },
          ticks: {
            color: "rgba(244, 247, 251, 0.52)",
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 7,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(255, 255, 255, 0.045)",
          },
          border: {
            display: false,
          },
          ticks: {
            color: "rgba(244, 247, 251, 0.52)",
            padding: 8,
          },
        },
      },
    }),
    []
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedDate(selectDate);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [selectDate]);

  useEffect(() => {
    const getUserToken = async () => {
      try {
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken?.toString();

        if (!accessToken) {
          navigate("/login");
          return;
        }

        setUserToken(accessToken);
      } catch (error) {
        console.error("Unable to retrieve authentication session:", error);
        navigate("/login");
      } finally {
        setAuthChecked(true);
      }
    };

    getUserToken();
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchAssetStatistics = async () => {
      if (!userToken) return;

      setStatsLoading(true);

      try {
        const response = await fetch(
          "https://api.weathervaneintel.com/api/api/assetStatistics",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify([
              {
                userToken,
                startDate: debouncedDate || "",
              },
            ]),
          }
        );

        if (response.status === 403) {
          navigate("/login");
          return;
        }

        if (!response.ok) {
          console.warn("API Error:", response.status, response.statusText);
          return;
        }

        const data = await response.json();

        setDashboardData({
          ...data,
          individualStats: data.comStatistics?.individual_stats || [],
          tickerData: data.assetTrends || [],
          comsChart: data.comsChart || null,
          timeLineData: data.allGroupTimeline || null,
          newsData: data.getNewsData || [],
          barData: data.barData || [],
          allStats: data.getTotals || {},
          comStatistics: data.comStatistics || null,
        });
      } catch (error) {
        console.error("assetStatistics error:", error);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchAssetStatistics();
  }, [userToken, debouncedDate, navigate]);

  useEffect(() => {
    const fetchNewsGroups = async () => {
      if (!userToken) return;

      try {
        const response = await fetch(
          "https://api.weathervaneintel.com/api/api/getNewsGroups",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify([{ userToken }]),
          }
        );

        if (response.status === 403) {
          navigate("/login");
          return;
        }

        if (!response.ok) {
          console.warn("API Error:", response.status, response.statusText);
          return;
        }

        const data = await response.json();
        setGroupData(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("getNewsGroups error:", error);
      }
    };

    fetchNewsGroups();
  }, [userToken, navigate]);

  useEffect(() => {
    const fetchWordCloud = async () => {
      if (!userToken) return;

      setWordCloudLoading(true);
      setThreatData(null);

      try {
        const response = await fetch(
          "https://api.weathervaneintel.com/api/api/wordCloud",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify([
              {
                startDate: debouncedDate,
                group: group === "none" ? "" : group,
                asset: asset === "none" ? "" : asset,
                userToken,
              },
            ]),
          }
        );

        if (response.status === 403) {
          navigate("/login");
          return;
        }

        if (!response.ok) {
          console.warn("API Error:", response.status, response.statusText);
          setThreatData({});
          return;
        }

        const data = await response.json();

        if (!data || typeof data !== "object" || Array.isArray(data)) {
          console.warn("Unexpected word-cloud response:", data);
          setThreatData({});
          return;
        }

        setThreatData(data);
      } catch (error) {
        console.error("wordCloud error:", error);
        setThreatData({});
      } finally {
        setWordCloudLoading(false);
      }
    };

    fetchWordCloud();
  }, [userToken, debouncedDate, group, asset, navigate]);

  const handleGroup = useCallback(
    (selectedGroup) => {
      setGroup(selectedGroup);
      setAsset("none");

      if (selectedGroup === "none") {
        setFilteredIndividualStats([]);
        setGroupAssets([]);
        return;
      }

      const selectedGroupData = groupData.find(
        (item) => item.groupName === selectedGroup
      );

      setGroupAssets(selectedGroupData?.assets || []);

      const stats = (dashboardData.individualStats || []).filter(
        (item) => item.group_name === selectedGroup
      );
      setFilteredIndividualStats(stats);
    },
    [groupData, dashboardData.individualStats]
  );

  const resetFilters = useCallback(() => {
    setGroup("none");
    setAsset("none");
    setSelectDate("");
    setGroupAssets([]);
    setFilteredIndividualStats([]);
  }, []);

const openChartPopOut = useCallback((chartType, data, title) => {
  if (!data) return;

  setChartPopOutData(data);
  setPopOutChartType(chartType);
  setChartPopOutTitle(title || "Expanded analytical view");
  setChartPopOutOpen(true);
}, []);

  const closeChartPopOut = useCallback(() => {
    setChartPopOutOpen(false);
  }, []);

  const getNewsTimelineData = useCallback(() => {
    if (!dashboardData.newsData?.length) return null;

    let labels = [];
    let datasets = [];

    if (!group || group === "none") {
      labels = dashboardData.newsData[0]?.group_dates || [];
      datasets = dashboardData.newsData
        .filter((item) => item.group_timeline_values?.some((value) => value > 0))
        .map((item) => ({
          label: item.name,
          data: item.group_timeline_values,
          tension: 0.3,
        }));
    } else {
      const selectedGroupData = dashboardData.newsData.find(
        (item) => item.name === group
      );

      if (!selectedGroupData) return null;

      labels = selectedGroupData.group_dates || [];
      datasets = (selectedGroupData.assets || [])
        .filter((item) => item.values?.some((value) => value > 0))
        .map((item) => ({
          label:
            item.name === "MainGroup MainGroup"
              ? `${group} Group Search`
              : item.name,
          data: item.values,
          tension: 0.3,
        }));
    }

    if (!labels.length || !datasets.length) return null;
    return { labels, datasets };
  }, [dashboardData.newsData, group]);

  const getWeeklyArticlesBarData = useCallback(() => {
    let cleaned = [];

    if (!group || group === "none") {
      cleaned = (dashboardData.barData || [])
        .filter(
          (item) =>
            item.total_news_articles_in_last_week !== null &&
            item.total_news_articles_in_last_week !== undefined
        )
        .map((item) => ({
          label: item.groupname,
          value: Number(item.total_news_articles_in_last_week) || 0,
        }));
    } else {
      cleaned = (dashboardData.individualStats || [])
        .filter((item) => item.group_name === group)
        .filter(
          (item) =>
            item.articles_last_week !== null &&
            item.articles_last_week !== undefined
        )
        .map((item) => {
          const name = `${item.firstname || ""} ${item.lastname || ""}`.trim();
          return {
            label:
              name === "MainGroup MainGroup"
                ? `${group} Group Search`
                : name,
            value: Number(item.articles_last_week) || 0,
          };
        });
    }

    cleaned.sort((a, b) => b.value - a.value);

    if (!cleaned.length) return null;

    return {
      labels: cleaned.map((item) => item.label),
      datasets: [
        {
          label: "Seven-day news volume",
          data: cleaned.map((item) => item.value),
          borderRadius: 6,
          borderSkipped: false,
        },
      ],
    };
  }, [dashboardData.barData, dashboardData.individualStats, group]);

  const getCommentsChartData = useCallback(() => {
    if (!dashboardData.comsChart) return null;

    const baseChart =
      !group || group === "none"
        ? dashboardData.comsChart.groupData
        : dashboardData.comsChart.assetData?.[group];

    if (!baseChart) return null;

    const chartData = {
      labels: [...(baseChart.labels || [])],
      datasets: (baseChart.datasets || [])
        .map((dataset) => ({
          ...dataset,
          data: [...(dataset.data || [])],
          tension: 0.3,
          label:
            group &&
              group !== "none" &&
              dataset.label === "MainGroup MainGroup"
              ? `${group} Group Search`
              : dataset.label,
        }))
        .filter((dataset) => dataset.data.some((value) => Number(value) !== 0)),
    };

    if (!chartData.labels.length || !chartData.datasets.length) return null;
    return chartData;
  }, [dashboardData.comsChart, group]);

  const tickerForWidget = useMemo(() => {
    const tickerData = dashboardData.tickerData || [];

    return tickerData.filter((item) => {
      const hasActivity =
        Number(item.last_day_coms) !== 0 ||
        Number(item.avg_coms_last_week) !== 0 ||
        Number(item.total_coms_last_week) !== 0;

      if (!hasActivity) return false;
      return !group || group === "none" || item.groupname === group;
    });
  }, [dashboardData.tickerData, group]);

  const commentsChartData = useMemo(
    () => getCommentsChartData(),
    [getCommentsChartData]
  );
  const newsTimelineData = useMemo(
    () => getNewsTimelineData(),
    [getNewsTimelineData]
  );
  const weeklyArticlesData = useMemo(
    () => getWeeklyArticlesBarData(),
    [getWeeklyArticlesBarData]
  );

  const filterPanel = (
    <div className="analytics-filter" ref={dropdownRef}>
      <button
        type="button"
        className="analytics-filter-toggle"
        onClick={() => setDropdownOpen((current) => !current)}
        aria-expanded={dropdownOpen}
        aria-haspopup="true"
        aria-controls="analytics-filter-panel"
      >
        Filter view
        <span aria-hidden="true">⌄</span>
      </button>

      {dropdownOpen ? (
        <div id="analytics-filter-panel" className="analytics-filter-panel">
          <div className="analytics-filter-header">
            <span>View controls</span>
            <strong>Filter analytical data</strong>
          </div>

          <form onSubmit={(event) => event.preventDefault()}>
            <div className="analytics-filter-field">
              <label htmlFor="groupSelect">Monitoring group</label>
              <select
                id="groupSelect"
                value={group}
                onChange={(event) => handleGroup(event.target.value)}
              >
                <option value="none">All monitoring groups</option>
                {groupData.map((item) => (
                  <option key={item.groupName} value={item.groupName}>
                    {item.groupName}
                  </option>
                ))}
              </select>
            </div>

            <div className="analytics-filter-field">
              <label htmlFor="groupAsset">Monitored asset</label>
              <select
                id="groupAsset"
                value={asset}
                onChange={(event) => setAsset(event.target.value)}
                disabled={!group || group === "none"}
              >
                <option value="none">
                  {!group || group === "none"
                    ? "Select a group first"
                    : "All assets in group"}
                </option>
                {groupAssets.map((item) => (
                  <option key={item.name} value={item.name}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="analytics-filter-field">
              <label htmlFor="selectDate">Start date</label>
              <input
                type="date"
                id="selectDate"
                value={selectDate}
                onChange={(event) => setSelectDate(event.target.value)}
              />
              <small>Leave blank to use the default reporting period.</small>
            </div>

            <div className="analytics-filter-actions">
              <button type="button" onClick={resetFilters}>
                Reset filters
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );

  if (!authChecked || userToken === null) {
    return (
      <main className="analytics-page">
        <DashboardLoadingState fullPage />
      </main>
    );
  }

  return (
    <main className="analytics-page">
      <div className="analytics-contour-background" aria-hidden="true" />

      <div className="analytics-shell">
        <section className="analytics-header">
          <div className="analytics-header-copy">
            <span className="analytics-eyebrow">Operational Analytics</span>
            <h1>Monitoring Overview</h1>
            <p>
              Review current collection volume, reporting trends, and
              activity-based risk indicators across your monitored environment.
            </p>
          </div>

          <div className="analytics-header-actions">{filterPanel}</div>
        </section>

        <section
          className="analytics-metrics-section"
          aria-label="Current monitoring totals"
        >
          <TopMetrics
            allStats={dashboardData.allStats}
            statsLoading={statsLoading}
          />
        </section>

        <div className="analytics-grid">
          <AnalyticsCard
            kicker="Collection activity"
            title="Discussions by monitored group"
            onExpand={
              commentsChartData
                ? () =>
    openChartPopOut(
      "LINE",
      commentsChartData,
      "Discussions by monitored group"
    )
                : null
            }
          >
            {statsLoading ? (
              <DashboardLoadingState />
            ) : !commentsChartData ? (
              <EmptyState onReviewAssets={() => navigate("/groups")} />
            ) : (
              <DisplayCommentsChart
                key={group || "all"}
                comsChart={commentsChartData}
              />
            )}
          </AnalyticsCard>

          <AnalyticsCard
            kicker="Analyst prioritization"
            title="Current risk indicators"
            infoText="These scores summarize recent activity and potentially relevant content. They are intended to support analyst prioritization and should not be treated as a final threat assessment."
            bodyClassName="analytics-risk-body"
          >
            {statsLoading ? (
              <DashboardLoadingState />
            ) : !dashboardData.individualStats?.length ? (
              <EmptyState onReviewAssets={() => navigate("/groups")} />
            ) : (
              <RiskWidget
                data={
                  filteredIndividualStats.length
                    ? filteredIndividualStats
                    : dashboardData.individualStats
                }
              />
            )}
          </AnalyticsCard>

          <AnalyticsCard
            kicker="Reporting environment"
            title="Seven-day news volume"
            onExpand={
              weeklyArticlesData
                ? () =>
    openChartPopOut(
      "BAR",
      weeklyArticlesData,
      "Seven-day news volume"
    )
                : null
            }
          >
            {statsLoading ? (
              <DashboardLoadingState />
            ) : !weeklyArticlesData ? (
              <EmptyState onReviewAssets={() => navigate("/groups")} />
            ) : (
              <Bar
                data={weeklyArticlesData}
                options={{
                  ...chartOptions,
                  plugins: {
                    ...chartOptions.plugins,
                    legend: {
                      ...chartOptions.plugins.legend,
                      display: false,
                    },
                  },
                  scales: {
                    ...chartOptions.scales,
                    x: {
                      ...chartOptions.scales.x,
                      ticks: {
                        ...chartOptions.scales.x.ticks,
                        autoSkip: false,
                      },
                    },
                  },
                }}
              />
            )}
          </AnalyticsCard>

          <AnalyticsCard
            kicker="Reporting trend"
            title="News activity over time"
            onExpand={
              newsTimelineData
                ? () =>
    openChartPopOut(
      "LINE",
      newsTimelineData,
      "News activity over time"
    )
                : null
            }
          >
            {statsLoading ? (
              <DashboardLoadingState />
            ) : !newsTimelineData ? (
              <EmptyState onReviewAssets={() => navigate("/groups")} />
            ) : (
              <Line data={newsTimelineData} options={chartOptions} />
            )}
          </AnalyticsCard>

          <AnalyticsCard
            kicker="Volume comparison"
            title="Discussion activity trends"
            bodyClassName="analytics-table-body"
          >
            {statsLoading ? (
              <DashboardLoadingState />
            ) : !tickerForWidget.length ? (
              <EmptyState onReviewAssets={() => navigate("/groups")} />
            ) : (
              <TickerWidget tickerData={tickerForWidget} />
            )}
          </AnalyticsCard>

          <section className="analytics-card">
            <header className="analytics-card-header">
              <div className="analytics-card-action">
                {threatData && Object.keys(threatData).length > 0 ? (
                  <button
                    type="button"
                    className="analytics-expand-button"
                    aria-label="Open terms within flagged content in expanded view"
                    title="Open expanded view"
                    onClick={() =>
  openChartPopOut(
    "WORDCLOUD",
    threatData,
    "Terms within flagged content"
  )
}
                  >
                    <OpenInNewIcon aria-hidden="true" />
                  </button>
                ) : null}
              </div>

              <div className="analytics-card-title">
                <span>Language environment</span>
                <h2>Terms within flagged content</h2>
              </div>

              <div className="analytics-card-meta" />
            </header>

            <StableWordCloud
              data={threatData}
              loading={wordCloudLoading}
              onReviewAssets={() => navigate("/groups")}
            />
          </section>
        </div>

        <PopOutChartModal
          isOpen={chartPopOutOpen}
          onClose={closeChartPopOut}
          chartData={chartPopOutData}
          type={popOutChartType}
          title={chartPopOutTitle}
        />
      </div>
    </main>
  );
};
